import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { BrandProvider } from '@/brand/BrandProvider'
import { VendorAuthProvider, useVendorAuth } from '@/auth/VendorAuthProvider'
import { AppShell } from '@/components/AppShell'
import { Splash } from '@/components/ui'
import { Login } from '@/screens/Login'
import { Onboarding } from '@/screens/Onboarding'
import { FirstLoginChange } from '@/screens/FirstLoginChange'
import { ResetPassword } from '@/screens/ResetPassword'
import { Dashboard } from '@/screens/Dashboard'
import { Catalog } from '@/screens/Catalog'
import { Crm } from '@/screens/Crm'
import { Calendar } from '@/screens/Calendar'
import { AdminHome } from '@/screens/admin/AdminHome'
import { AdminVendors } from '@/screens/admin/AdminVendors'
import { AdminCollections } from '@/screens/admin/AdminCollections'
import { AdminDashboard } from '@/screens/admin/AdminDashboard'
import { AdminAdmins } from '@/screens/admin/AdminAdmins'
import { AdminBrand } from '@/screens/admin/AdminBrand'
import { AdminCalendar } from '@/screens/admin/AdminCalendar'
import { AdminBanners } from '@/screens/admin/AdminBanners'
import { AdminConnection } from '@/screens/admin/AdminConnection'

/* ---------------- Guards ---------------- */

function RequireVendor() {
  const { vendor, loading, isAccountSession } = useVendorAuth()
  if (loading) return <Splash />
  // Una sesión de cuenta no es una sesión de tienda: su lugar es el selector. Sin esto,
  // el primer request de cualquier pantalla devuelve 401 y la destruye a mitad de camino.
  if (isAccountSession) return <Navigate to="/onboarding" replace />
  if (!vendor) return <Navigate to="/login" replace />
  if (vendor.isFirstLogin) return <Navigate to="/primer-ingreso" replace />
  return <Outlet />
}

function RequireAdmin() {
  const { vendor } = useVendorAuth()
  if (!vendor?.isAdmin) return <Navigate to="/" replace />
  return <Outlet />
}

// Sesión de cuenta: no hay tenant ni vendedor todavía, así que no pasa por RequireVendor.
function RequireAccount({ children }: { children: React.ReactNode }) {
  const { isAccountSession, loading } = useVendorAuth()
  if (loading) return <Splash />
  if (!isAccountSession) return <Navigate to="/login" replace />
  return <>{children}</>
}

// Con el tenant en provisioning no hay nada que hacer en el panel salvo cargar la conexión
// (los vendedores no pueden ni entrar), así que el resto del admin redirige ahí.
function AdminGate() {
  const { vendor } = useVendorAuth()
  if (!vendor?.isAdmin) return <Navigate to="/" replace />
  if (vendor.tenantStatus === 'provisioning') return <Navigate to="/admin/conexion" replace />
  return <Outlet />
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { vendor, loading, isAccountSession } = useVendorAuth()
  if (loading) return <Splash />
  if (isAccountSession) return <Navigate to="/onboarding" replace />
  if (vendor) return <Navigate to={vendor.isFirstLogin ? '/primer-ingreso' : '/'} replace />
  return <>{children}</>
}

// El admin (Koru) no es un vendedor: no tiene Dashboard de vendedor (rompería contra
// /api/dashboard, que busca al usuario en D1). Lo mandamos a su panel.
function VendorHome() {
  const { vendor } = useVendorAuth()
  if (vendor?.isAdmin) return <Navigate to="/admin" replace />
  return <Dashboard />
}

function FirstLoginRoute({ children }: { children: React.ReactNode }) {
  const { vendor, loading } = useVendorAuth()
  if (loading) return <Splash />
  if (!vendor) return <Navigate to="/login" replace />
  if (!vendor.isFirstLogin) return <Navigate to="/" replace />
  return <>{children}</>
}

/* ---------------- Routes ---------------- */

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route path="/primer-ingreso" element={<FirstLoginRoute><FirstLoginChange /></FirstLoginRoute>} />
        <Route path="/onboarding" element={<RequireAccount><Onboarding /></RequireAccount>} />

        <Route element={<RequireVendor />}>
          <Route element={<AppShell />}>
            <Route index element={<VendorHome />} />
            <Route path="catalogo" element={<Catalog />} />
            <Route path="clientes" element={<Crm />} />
            <Route path="calendario" element={<Calendar />} />
            {/* La conexión queda fuera de AdminGate: es el destino del redirect. */}
            <Route path="admin/conexion" element={<RequireAdmin />}>
              <Route index element={<AdminConnection />} />
            </Route>
            <Route path="admin" element={<AdminGate />}>
              <Route index element={<AdminHome />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="vendedores" element={<AdminVendors />} />
              <Route path="colecciones" element={<AdminCollections />} />
              <Route path="admins" element={<AdminAdmins />} />
              <Route path="marca" element={<AdminBrand />} />
              <Route path="calendario" element={<AdminCalendar />} />
              <Route path="banners" element={<AdminBanners />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default function App() {
  return (
    <BrandProvider>
      <VendorAuthProvider>
        <AppRoutes />
      </VendorAuthProvider>
    </BrandProvider>
  )
}
